/**
 * Hand-off state between the quiz and the in-app paywall. Nothing goes through the URL:
 * the quiz writes here before navigating to `/paywall`, the paywall and post-payment read it.
 * Lives in sessionStorage, like the post-payment context and the WebPaywalls stores: one tab = one funnel run.
 */
export const FUNNEL_CONTEXT_KEY = '[[funnel]]';

export interface FunnelContext {
  onboardingId?: string;
  /** CMS paywall slug; equals the quiz variant by convention. */
  paywallSlug: string;
  enteredPaywallAt?: string;
  /** Quiz → paywall hand-off animation, taken from the quiz layout's page transitions (default: slide). */
  handoffTransition?: HandoffTransition;
}

export type HandoffTransition = 'fade' | 'slide';

const isHandoffTransition = (value: unknown): value is HandoffTransition => value === 'fade' || value === 'slide';

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object';

export const readFunnelContext = (storage: Storage = sessionStorage): FunnelContext | null => {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(FUNNEL_CONTEXT_KEY) || 'null');
    if (!isRecord(parsed) || typeof parsed.paywallSlug !== 'string') return null;
    return {
      paywallSlug: parsed.paywallSlug,
      onboardingId: typeof parsed.onboardingId === 'string' ? parsed.onboardingId : undefined,
      enteredPaywallAt: typeof parsed.enteredPaywallAt === 'string' ? parsed.enteredPaywallAt : undefined,
      handoffTransition: isHandoffTransition(parsed.handoffTransition) ? parsed.handoffTransition : undefined,
    };
  } catch {
    return null;
  }
};

export const writeFunnelContext = (context: FunnelContext, storage: Storage = sessionStorage): void => {
  try {
    storage.setItem(FUNNEL_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // Storage unavailable: the paywall falls back to the variant from entry params.
  }
};
